import React, { useState, useRef } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Camera, Upload, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import imageCompression from 'browser-image-compression';

interface AvatarUploaderProps {
  employeeId: string;
  currentAvatarUrl?: string | null;
  employeeName: string;
  onAvatarUpdate: (url: string | null) => void;
  className?: string;
}

export const AvatarUploader: React.FC<AvatarUploaderProps> = ({
  employeeId,
  currentAvatarUrl,
  employeeName,
  onAvatarUpdate,
  className = "h-16 w-16"
}) => {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const initials = employeeName
    .split(' ')
    .map(name => name.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size must be less than 2MB');
      return;
    }

    try {
      setUploading(true);

      // Compress the image
      const compressedFile = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 512,
        useWebWorker: true,
        fileType: 'image/webp'
      });

      // Create preview
      const preview = URL.createObjectURL(compressedFile);
      setPreviewUrl(preview);

      // Upload to Supabase Storage
      const fileExt = 'webp';
      const fileName = `employee-${employeeId}.${fileExt}`;
      const filePath = fileName;

      // Delete existing avatar if it exists
      if (currentAvatarUrl) {
        const existingPath = currentAvatarUrl.split('/').pop();
        if (existingPath) {
          await supabase.storage
            .from('employee-avatars')
            .remove([existingPath]);
        }
      }

      // Upload new avatar
      const { error: uploadError } = await supabase.storage
        .from('employee-avatars')
        .upload(filePath, compressedFile, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data } = supabase.storage
        .from('employee-avatars')
        .getPublicUrl(filePath);

      const avatarUrl = data.publicUrl;

      // Update employee profile
      const { error: updateError } = await supabase
        .from('employee_profiles')
        .update({ avatar_url: avatarUrl })
        .eq('employee_id', employeeId);

      if (updateError) throw updateError;

      onAvatarUpdate(avatarUrl);
      toast.success('Avatar updated successfully!');
      
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error('Failed to upload avatar');
      setPreviewUrl(null);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveAvatar = async () => {
    if (!currentAvatarUrl) return;

    try {
      setUploading(true);

      // Remove from storage
      const fileName = currentAvatarUrl.split('/').pop();
      if (fileName) {
        await supabase.storage
          .from('employee-avatars')
          .remove([fileName]);
      }

      // Update employee profile
      const { error } = await supabase
        .from('employee_profiles')
        .update({ avatar_url: null })
        .eq('employee_id', employeeId);

      if (error) throw error;

      onAvatarUpdate(null);
      setPreviewUrl(null);
      toast.success('Avatar removed successfully!');
      
    } catch (error) {
      console.error('Error removing avatar:', error);
      toast.error('Failed to remove avatar');
    } finally {
      setUploading(false);
    }
  };

  const displayUrl = previewUrl || currentAvatarUrl;

  return (
    <div className="relative group">
      <Avatar className={className}>
        <AvatarImage src={displayUrl || ''} alt={employeeName} />
        <AvatarFallback className="text-lg">{initials}</AvatarFallback>
      </Avatar>

      {/* Upload overlay */}
      <div className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        <div className="flex gap-1">
          <Button
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0 text-white hover:bg-white/20"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <div className="h-3 w-3 animate-spin rounded-full border border-white border-t-transparent" />
            ) : (
              <Camera className="h-3 w-3" />
            )}
          </Button>
          
          {(currentAvatarUrl || previewUrl) && (
            <Button
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0 text-white hover:bg-red-500/50"
              onClick={handleRemoveAvatar}
              disabled={uploading}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
};